import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, TextField,
  Avatar, IconButton, Divider, Tab, Tabs, FormControl, InputLabel,
  Select, MenuItem, Chip, OutlinedInput, Paper, ListItemText,
  FormHelperText, CircularProgress, Snackbar, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AddCircleOutline as AddIcon,
  DeleteOutline as DeleteIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  Interests as InterestsIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import { getLanguageOptions, PROFICIENCY_LEVELS } from '../config/languagesConfig';

// Language + proficiency options come from the central config (includes es-419, pt-BR).
const languageOptions = getLanguageOptions();
const proficiencyLevels = PROFICIENCY_LEVELS;

const ProfilePage = () => {
  const { user, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableInterests, setAvailableInterests] = useState([]);
  const [interestsByCategory, setInterestsByCategory] = useState({});

  // NEW: Language selection dialog state
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [selectedNewLanguage, setSelectedNewLanguage] = useState('');

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    password: '',
    native_language: user?.native_language || 'en',
    languages: user?.languages || [],
    interests: user?.interests?.map(i => i.id) || []
  });
  const [formErrors, setFormErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch interests from the API
  useEffect(() => {
    const fetchInterests = async () => {
      try {
        const { data } = await apiClient.auth.getInterests();
        setAvailableInterests(data.interests || []);

        // Group interests by category
        const grouped = {};
        data.interests.forEach(interest => {
          if (!grouped[interest.category]) {
            grouped[interest.category] = [];
          }
          grouped[interest.category].push(interest);
        });
        setInterestsByCategory(grouped);
      } catch (error) {
        console.error('Error fetching interests:', error);
      }
    };

    fetchInterests();
  }, []);

  // Update formData when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        password: '',
        native_language: user.native_language || 'en',
        languages: user.languages || [],
        interests: user.interests?.map(i => i.id) || []
      });
    }
  }, [user]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (editing) {
      // Cancel editing - reset form data
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        password: '',
        native_language: user.native_language || 'en',
        languages: user.languages || [],
        interests: user.interests?.map(i => i.id) || []
      });
      setFormErrors({});
    }
    setEditing(!editing);
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle language selection
  const handleLanguageChange = (e) => {
    const { value } = e.target;
    // Filter out any duplicates and ensure format is correct
    const languages = value.map(item => {
      if (typeof item === 'string') {
        const [code, level] = item.split('-');
        return { language_code: code, proficiency_level: level || 'A1' };
      }
      return item;
    });

    setFormData(prev => ({
      ...prev,
      languages
    }));
  };

  // Handle interest selection
  const handleInterestChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      interests: value
    }));
  };

  // FIXED: Open language selection dialog instead of automatically adding first language
  const handleAddLanguage = () => {
    // Find available languages that aren't already selected
    const availableLangs = languageOptions
      .filter(lang => lang.code !== formData.native_language)
      .filter(lang => !formData.languages.some(l => l.language_code === lang.code));

    if (availableLangs.length > 0) {
      setSelectedNewLanguage(''); // Reset selection
      setLanguageDialogOpen(true);
    } else {
      // Show notification if no more languages available
      setNotification({
        open: true,
        message: 'No more languages available to add',
        severity: 'info'
      });
    }
  };

  // FIXED: Handle confirmation of new language selection
  const handleConfirmLanguageAdd = () => {
    if (selectedNewLanguage) {
      const newLang = {
        language_code: selectedNewLanguage,
        proficiency_level: 'A1'
      };

      setFormData(prev => ({
        ...prev,
        languages: [...prev.languages, newLang]
      }));

      setLanguageDialogOpen(false);
      setSelectedNewLanguage('');
    }
  };

  // Handle cancel language selection
  const handleCancelLanguageAdd = () => {
    setLanguageDialogOpen(false);
    setSelectedNewLanguage('');
  };

  // Remove a language
  const handleRemoveLanguage = (index) => {
    const updatedLanguages = [...formData.languages];
    updatedLanguages.splice(index, 1);

    setFormData(prev => ({
      ...prev,
      languages: updatedLanguages
    }));
  };

  // Update language level
  const handleUpdateLanguageLevel = (index, level) => {
    const updatedLanguages = [...formData.languages];
    updatedLanguages[index] = {
      ...updatedLanguages[index],
      proficiency_level: level
    };

    setFormData(prev => ({
      ...prev,
      languages: updatedLanguages
    }));
  };

  // Update language code
  const handleUpdateLanguageCode = (index, code) => {
    const updatedLanguages = [...formData.languages];
    updatedLanguages[index] = {
      ...updatedLanguages[index],
      language_code: code
    };

    setFormData(prev => ({
      ...prev,
      languages: updatedLanguages
    }));
  };

  // Save profile
  const handleSaveProfile = async () => {
    setLoading(true);

    try {
      const response = await updateProfile(formData);
      setNotification({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setNotification({
        open: true,
        message: 'Failed to update profile',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Get available languages for new language selection
  const getAvailableLanguagesForDialog = () => {
    return languageOptions
      .filter(lang => lang.code !== formData.native_language)
      .filter(lang => !formData.languages.some(l => l.language_code === lang.code));
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Account
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: 100,
                      height: 100,
                      mx: 'auto',
                      mb: 2,
                      bgcolor: 'primary.main'
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 50 }} />
                  </Avatar>

                  <Typography variant="h6" gutterBottom>
                    {user?.first_name} {user?.last_name}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant={editing ? "outlined" : "contained"}
                      startIcon={<EditIcon />}
                      onClick={handleEditToggle}
                      fullWidth
                    >
                      {editing ? 'Cancel' : 'Edit Profile'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              {editing ? (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Edit Account Information
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          error={!!formErrors.first_name}
                          helperText={formErrors.first_name}
                          disabled={loading}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          error={!!formErrors.last_name}
                          helperText={formErrors.last_name}
                          disabled={loading}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          error={!!formErrors.email}
                          helperText={formErrors.email}
                          disabled={loading}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="New Password (leave blank to keep current)"
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleChange}
                          error={!!formErrors.password}
                          helperText={formErrors.password}
                          disabled={loading}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel id="native-language-label">Native Language</InputLabel>
                          <Select
                            labelId="native-language-label"
                            id="native_language"
                            name="native_language"
                            value={formData.native_language}
                            onChange={handleChange}
                            disabled={loading}
                            label="Native Language"
                          >
                            {languageOptions.map(language => (
                              <MenuItem key={language.code} value={language.code}>
                                {language.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleEditToggle}
                        disabled={loading}
                      >
                        Cancel
                      </Button>

                      <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={24} /> : <SaveIcon />}
                        onClick={handleSaveProfile}
                        disabled={loading}
                      >
                        Save Changes
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Account Information
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Name
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body1">
                            {user?.first_name} {user?.last_name}
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Email
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body1">
                            {user?.email}
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Native Language
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body1">
                            {languageOptions.find(l => l.code === user?.native_language)?.name || user?.native_language}
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Member Since
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body1">
                            {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        );

      case 1: // Languages
        return (
          <Card>
            <CardContent>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6">
                  Languages I'm Learning
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!editing ? (
                    <Button
                      startIcon={<EditIcon />}
                      onClick={handleEditToggle}
                    >
                      Edit Languages
                    </Button>
                  ) : (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={handleAddLanguage}
                      disabled={
                        loading ||
                        formData.languages.length >= languageOptions.length - 1 // Can't add more languages than available
                      }
                    >
                      Add Language
                    </Button>
                  )}
                </Box>
              </Box>

              {formData.languages.length > 0 ? (
                <Grid container spacing={2}>
                  {formData.languages.map((language, index) => (
                    <Grid item xs={12} sm={6} key={`${language.language_code}-${index}`}>
                      <Paper sx={{ p: 2 }}>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start'
                        }}>
                          <Typography variant="subtitle1">
                            {languageOptions.find(l => l.code === language.language_code)?.name || language.language_code}
                          </Typography>

                          {editing && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveLanguage(index)}
                              aria-label="remove language"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>

                        {editing ? (
                          <Box sx={{ mt: 2 }}>
                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                              <InputLabel>Language</InputLabel>
                              <Select
                                value={language.language_code}
                                onChange={(e) => handleUpdateLanguageCode(index, e.target.value)}
                                label="Language"
                              >
                                {languageOptions
                                  .filter(lang =>
                                    lang.code !== formData.native_language &&
                                    (lang.code === language.language_code ||
                                     !formData.languages.some(l => l.language_code === lang.code))
                                  )
                                  .map(lang => (
                                    <MenuItem key={lang.code} value={lang.code}>
                                      {lang.name}
                                    </MenuItem>
                                  ))
                                }
                              </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                              <InputLabel>Proficiency Level</InputLabel>
                              <Select
                                value={language.proficiency_level}
                                onChange={(e) => handleUpdateLanguageLevel(index, e.target.value)}
                                label="Proficiency Level"
                              >
                                {proficiencyLevels.map(level => (
                                  <MenuItem key={level.value} value={level.value}>
                                    {level.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {proficiencyLevels.find(l => l.value === language.proficiency_level)?.label || language.proficiency_level}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No languages added yet. {editing && 'Click "Add Language" to get started.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        );

      case 2: // Interests
        return (
          <Card>
            <CardContent>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6">
                  My Interests
                </Typography>

                {!editing && (
                  <Button
                    startIcon={<EditIcon />}
                    onClick={handleEditToggle}
                  >
                    Edit Interests
                  </Button>
                )}
              </Box>

              {editing ? (
                <FormControl fullWidth>
                  <InputLabel id="interests-label">Select Interests</InputLabel>
                  <Select
                    labelId="interests-label"
                    multiple
                    value={formData.interests}
                    onChange={handleInterestChange}
                    input={<OutlinedInput label="Select Interests" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const interest = availableInterests.find(i => i.id === value);
                          return (
                            <Chip key={value} label={interest?.name || value} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {Object.entries(interestsByCategory).map(([category, interests]) => [
                      <MenuItem key={category} disabled>
                        <Typography variant="subtitle2" color="primary">
                          {category}
                        </Typography>
                      </MenuItem>,
                      ...interests.map((interest) => (
                        <MenuItem key={interest.id} value={interest.id}>
                          <ListItemText primary={interest.name} sx={{ pl: 2 }} />
                        </MenuItem>
                      ))
                    ])}
                  </Select>
                </FormControl>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {user?.interests?.length > 0 ? (
                    user.interests.map((interest) => (
                      <Chip
                        key={interest.id}
                        label={interest.name}
                        variant="outlined"
                        icon={<InterestsIcon />}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No interests selected yet.
                    </Typography>
                  )}
                </Box>
              )}

              {editing && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleEditToggle}
                    disabled={loading}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={24} /> : <SaveIcon />}
                    onClick={handleSaveProfile}
                    disabled={loading}
                  >
                    Save Changes
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ pb: { xs: 10, md: 2 } }}> {/* FIXED: Added bottom padding for mobile */}
      <Typography variant="h4" gutterBottom>
        My Profile
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="profile tabs">
          <Tab label="Account" />
          <Tab label="Languages" />
          <Tab label="Interests" />
        </Tabs>
      </Box>

      {renderTabContent()}

      {editing && activeTab !== 0 && (
        <Box sx={{
          mt: 3,
          mb: { xs: 8, md: 0 }, // FIXED: Extra bottom margin for mobile
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleEditToggle}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={24} /> : <SaveIcon />}
            onClick={handleSaveProfile}
            disabled={loading}
          >
            Save Changes
          </Button>
        </Box>
      )}

      {/* FIXED: Language Selection Dialog */}
      <Dialog open={languageDialogOpen} onClose={handleCancelLanguageAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Select a Language to Add</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Choose Language</InputLabel>
            <Select
              value={selectedNewLanguage}
              onChange={(e) => setSelectedNewLanguage(e.target.value)}
              label="Choose Language"
            >
              {getAvailableLanguagesForDialog().map(lang => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLanguageAdd}>Cancel</Button>
          <Button
            onClick={handleConfirmLanguageAdd}
            variant="contained"
            disabled={!selectedNewLanguage}
          >
            Add Language
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;